import axios from 'axios';

/**
 * TaxStatusCategory - Categoría de condición tributaria
 */
export interface TaxStatusCategory {
  id: string;
  name: string;
  countryCode?: string | null;
  countryName?: string | null;
  isSystem: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * DTO para crear nueva categoría
 */
export interface CreateTaxStatusCategoryDto {
  name: string;
  countryCode?: string;
  countryName?: string;
}

/**
 * Base URL del API
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Instancia de axios configurada con interceptors para autenticación
 */
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor para agregar el token JWT a todas las peticiones
api.interceptors.request.use(config => {
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
      const token = parsed.state?.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error parsing auth storage:', error);
    }
  }
  return config;
});

/**
 * API de categorías de tax status
 */
export const taxStatusApi = {
  /**
   * Lista todas las categorías
   * @param countryCode - (Opcional) Filtrar por código de país
   * @returns Array de categorías
   */
  async list(countryCode?: string): Promise<TaxStatusCategory[]> {
    const params = countryCode ? { countryCode } : {};
    const response = await api.get<TaxStatusCategory[]>('/auth/tax-status-categories', { params });
    return response.data;
  },

  /**
   * Crea una nueva categoría personalizada
   * @param data - Datos de la nueva categoría
   * @returns Categoría creada
   */
  async create(data: CreateTaxStatusCategoryDto): Promise<TaxStatusCategory> {
    const response = await api.post<TaxStatusCategory>('/auth/tax-status-categories', data);
    return response.data;
  },

  /**
   * Elimina una categoría personalizada
   * @param id - ID de la categoría
   * @returns Mensaje de confirmación
   */
  async delete(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/auth/tax-status-categories/${id}`);
    return response.data;
  },
};
